import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import type { Business, LeadFilter, MapState, User, Client, WebsiteBuild, Notification, Coordinates } from '../types';

interface MapStore {
  // Map state
  mapState: MapState;
  setMapState: (state: Partial<MapState>) => void;
  resetMapState: () => void;
  
  // Lead data
  leads: Business[];
  selectedLead: Business | null;
  setLeads: (leads: Business[]) => void;
  addLeads: (leads: Business[]) => void;
  setSelectedLead: (lead: Business | null) => void;
  updateLead: (id: string, updates: Partial<Business>) => void;
  
  // Filters
  filters: LeadFilter;
  setFilters: (filters: Partial<LeadFilter>) => void;
  resetFilters: () => void;
  
  // UI state
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  /** Map controls bottom sheet, opened from the button beside the logo. */
  controlsOpen: boolean;
  setControlsOpen: (open: boolean) => void;
  toggleControls: () => void;
  rightPanelOpen: boolean;
  setRightPanelOpen: (open: boolean) => void;
  rightPanelTab: 'details' | 'website' | 'contact' | 'analytics';
  setRightPanelTab: (tab: 'details' | 'website' | 'contact' | 'analytics') => void;
  
  // Loading states
  isLoadingLeads: boolean;
  setLoadingLeads: (loading: boolean) => void;
  isBuildingWebsite: boolean;
  setBuildingWebsite: (building: boolean) => void;
  
  // Map interactions
  hoveredLeadId: string | null;
  setHoveredLeadId: (id: string | null) => void;
  clickedLeadId: string | null;
  setClickedLeadId: (id: string | null) => void;
  
  // View mode
  viewMode: 'map' | 'list' | 'street' | '3d';
  setViewMode: (mode: 'map' | 'list' | 'street' | '3d') => void;
}

const defaultMapState: MapState = {
  center: { lat: 40.7128, lng: -74.0060 }, // NYC default
  zoom: 14,
  bearing: 0,
  pitch: 45,
  style: 'streets',
  show3DBuildings: true,
  showStreetView: false,
};

const defaultFilters: LeadFilter = {
  categories: [],
  subcategories: [],
  leadTypes: ['no-website', 'marketing', 'b2b', 'multiple'],
  minLeadScore: 0,
  maxLeadScore: 100,
  hasWebsite: null,
  minRating: 0,
  maxRating: 5,
  minReviews: 0,
  maxReviews: 10000,
  radius: 5000,
  center: { lat: 40.7128, lng: -74.0060 },
  keywords: [],
};

export const useMapStore = create<MapStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Map state
        mapState: defaultMapState,
        setMapState: (state) => set((prev) => ({ mapState: { ...prev.mapState, ...state } })),
        resetMapState: () => set({ mapState: defaultMapState }),
        
        // Lead data
        leads: [],
        selectedLead: null,
        setLeads: (leads) => set({ leads }),
        addLeads: (leads) => set((prev) => ({ leads: [...prev.leads, ...leads] })),
        setSelectedLead: (lead) => set({ selectedLead: lead }),
        updateLead: (id, updates) => set((prev) => ({
          leads: prev.leads.map((l) => l.id === id ? { ...l, ...updates } : l),
          selectedLead: prev.selectedLead?.id === id ? { ...prev.selectedLead, ...updates } : prev.selectedLead,
        })),
        
        // Filters
        filters: defaultFilters,
        setFilters: (filters) => set((prev) => ({ filters: { ...prev.filters, ...filters } })),
        resetFilters: () => set({ filters: defaultFilters }),
        
        // UI state
        sidebarOpen: true,
        setSidebarOpen: (open) => set({ sidebarOpen: open }),
        toggleSidebar: () => set((prev) => ({ sidebarOpen: !prev.sidebarOpen })),
        controlsOpen: false,
        setControlsOpen: (open) => set({ controlsOpen: open }),
        toggleControls: () => set((prev) => ({ controlsOpen: !prev.controlsOpen })),
        rightPanelOpen: false,
        setRightPanelOpen: (open) => set({ rightPanelOpen: open }),
        rightPanelTab: 'details',
        setRightPanelTab: (tab) => set({ rightPanelTab: tab }),
        
        // Loading states
        isLoadingLeads: false,
        setLoadingLeads: (loading) => set({ isLoadingLeads: loading }),
        isBuildingWebsite: false,
        setBuildingWebsite: (building) => set({ isBuildingWebsite: building }),
        
        // Map interactions
        hoveredLeadId: null,
        setHoveredLeadId: (id) => set({ hoveredLeadId: id }),
        clickedLeadId: null,
        setClickedLeadId: (id) => set({ clickedLeadId: id }),
        
        // View mode
        viewMode: 'map',
        setViewMode: (mode) => set({ viewMode: mode }),
      }),
      {
        name: 'leadmap3d-map-store',
        partialize: (state) => ({
          mapState: state.mapState,
          filters: state.filters,
          sidebarOpen: state.sidebarOpen,
          viewMode: state.viewMode,
        }),
      }
    )
  )
);

// User store
interface UserStore {
  user: User | null;
  setUser: (user: User | null) => void;
  updateUser: (updates: Partial<User>) => void;
  clients: Client[];
  setClients: (clients: Client[]) => void;
  addClient: (client: Client) => void;
  createClient: (client: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => Client;
  updateClient: (id: string, updates: Partial<Client>) => void;
  websiteBuilds: WebsiteBuild[];
  setWebsiteBuilds: (builds: WebsiteBuild[]) => void;
  addWebsiteBuild: (build: WebsiteBuild) => void;
  updateWebsiteBuild: (id: string, updates: Partial<WebsiteBuild>) => void;
  notifications: Notification[];
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  unreadCount: number;
}

export const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        setUser: (user) => set({ user }),
        updateUser: (updates) => set((prev) => ({ user: prev.user ? { ...prev.user, ...updates } : null })),
        
        clients: [],
        setClients: (clients) => set({ clients }),
        addClient: (client) => set((prev) => ({ clients: [client, ...prev.clients] })),
        createClient: (clientData) => {
          const newClient: Client = {
            ...clientData,
            id: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          set((prev) => ({ clients: [newClient, ...prev.clients] }));
          return newClient;
        },
        updateClient: (id, updates) => set((prev) => ({
          clients: prev.clients.map((c) => c.id === id ? { ...c, ...updates } : c),
        })),
        
        websiteBuilds: [],
        setWebsiteBuilds: (builds) => set({ websiteBuilds: builds }),
        addWebsiteBuild: (build) => set((prev) => ({ websiteBuilds: [build, ...prev.websiteBuilds] })),
        updateWebsiteBuild: (id, updates) => set((prev) => ({
          websiteBuilds: prev.websiteBuilds.map((b) => b.id === id ? { ...b, ...updates } : b),
        })),
        
        notifications: [],
        setNotifications: (notifications) => set({ notifications }),
        addNotification: (notification) => set((prev) => ({ 
          notifications: [notification, ...prev.notifications].slice(0, 50) 
        })),
        markNotificationRead: (id) => set((prev) => ({
          notifications: prev.notifications.map((n) => n.id === id ? { ...n, read: true } : n),
        })),
        markAllNotificationsRead: () => set((prev) => ({
          notifications: prev.notifications.map((n) => ({ ...n, read: true })),
        })),
        get unreadCount() {
          return get().notifications.filter((n) => !n.read).length;
        },
      }),
      {
        name: 'leadmap3d-user-store',
        partialize: (state) => ({
          user: state.user,
          clients: state.clients,
          websiteBuilds: state.websiteBuilds,
        }),
      }
    )
  )
);

// Search/Location store
interface SearchStore {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: Business[];
  setSearchResults: (results: Business[]) => void;
  searchHistory: string[];
  addToSearchHistory: (query: string) => void;
  clearSearchHistory: () => void;
  recentLocations: Coordinates[];
  addRecentLocation: (location: Coordinates) => void;
  geocodingResults: any[];
  setGeocodingResults: (results: any[]) => void;
}

export const useSearchStore = create<SearchStore>()(
  devtools(
    persist(
      (set, get) => ({
        searchQuery: '',
        setSearchQuery: (query) => set({ searchQuery: query }),
        searchResults: [],
        setSearchResults: (results) => set({ searchResults: results }),
        searchHistory: [],
        addToSearchHistory: (query) => set((prev) => ({
          searchHistory: [query, ...prev.searchHistory.filter((q) => q !== query)].slice(0, 10),
        })),
        clearSearchHistory: () => set({ searchHistory: [] }),
        recentLocations: [],
        addRecentLocation: (location) => set((prev) => ({
          recentLocations: [location, ...prev.recentLocations.filter((l) => 
            Math.abs(l.lat - location.lat) > 0.001 || Math.abs(l.lng - location.lng) > 0.001
          )].slice(0, 10),
        })),
        geocodingResults: [],
        setGeocodingResults: (results) => set({ geocodingResults: results }),
      }),
      {
        name: 'leadmap3d-search-store',
      }
    )
  )
);